import type { SavedDesign } from '@rh/shared/schemas/design'
import {
  applyDesignBrandColor,
  DEFAULT_DESIGN,
} from '@rh/shared/templates/default-design'
import { designFingerprint } from '@rh/shared/utils/design-fingerprint'
import type { CustomizeData } from '@rh/shared/templates/customize-to-design'

/**
 * True when the agency hasn't made the design their own yet: either no
 * saved design at all, or exactly the design onboarding seeded. Those
 * agencies get the setup wizard auto-opened on /customize and the "start
 * here" nudge on the workspace.
 *
 * Agencies seeded since designSeedFingerprint existed carry the exact
 * fingerprint of their seed (which may be an admin-uploaded default rather
 * than the checked-in DEFAULT_DESIGN — see the server's
 * default-design-service), so those compare by fingerprint. Older agencies
 * fall back to comparing against the stock DEFAULT_DESIGN seed (brand color
 * substituted); if such a seed predates a DEFAULT_DESIGN change it simply
 * won't compare equal — that agency keeps the button-only path, which is
 * acceptable.
 */
export function isUnconfiguredDesign(preferences: {
  design?: SavedDesign
  customize?: unknown
  designSeedFingerprint?: string
}): boolean {
  if (!preferences.design) return true
  if (preferences.designSeedFingerprint) {
    return (
      designFingerprint(preferences.design) ===
      preferences.designSeedFingerprint
    )
  }
  const brandColor =
    (preferences.customize as CustomizeData | undefined)?.borderColor ??
    '#000000'
  return (
    JSON.stringify(preferences.design) ===
    JSON.stringify(applyDesignBrandColor(DEFAULT_DESIGN, brandColor))
  )
}

/** Set when the setup wizard is completed or dismissed — stops auto-open. */
export const wizardDismissedKey = (agencyId: string) =>
  `rh-design-wizard-dismissed:${agencyId}`

/**
 * Set when the workspace "start here" nudge is dismissed, or on any visit
 * to /customize (once they've found the page, the nudge's job is done).
 */
export const customizeNudgeDismissedKey = (agencyId: string) =>
  `rh-customize-nudge-dismissed:${agencyId}`
